import { useEffect, useMemo, useRef, useState, type FC, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Tooltip } from 'antd';
import { trackStatTooltipOpen, trackStatCtaClick } from '@/utils/analytics';
import {
  CalendarOutlined,
  RocketOutlined,
  TeamOutlined,
  ThunderboltOutlined,
  DollarOutlined,
  TrophyOutlined,
  PieChartOutlined,
  InfoCircleOutlined,
  ArrowRightOutlined,
} from '@ant-design/icons';

interface StatCardProps {
  value: string;
  label: string;
  hint?: string;
  /** Multi-line string shown on hover. Supports `\n` as line breaks. */
  tooltip?: string;
  /** Optional CTA link rendered inside the tooltip. */
  tooltipLink?: { label: string; href: string };
}

/** Pick a line-icon based on the label semantics. Falls back to a trophy. */
const iconForLabel = (label: string): ReactNode => {
  const l = label.toLowerCase();
  if (/year|experience/.test(l)) return <CalendarOutlined />;
  if (/project|deliver|ship/.test(l)) return <RocketOutlined />;
  if (/user|customer|client|served/.test(l)) return <TeamOutlined />;
  if (/time|speed|delivery|launch|market|faster/.test(l)) return <ThunderboltOutlined />;
  if (/revenue|earn|million|sale|\$/.test(l)) return <DollarOutlined />;
  if (/industr|sector|domain|vertical/.test(l)) return <PieChartOutlined />;
  return <TrophyOutlined />;
};

/**
 * Module-level memory of which stat labels have already run their count-up
 * animation in this SPA session. Survives component remounts (e.g. when the
 * user returns to Home from a project detail page) so numbers don't run
 * through 0 → target every time.
 */
const animatedStatLabels = new Set<string>();

/**
 * Parse "10M+", "↓ 50%", "6+", "$10K+" into {prefix, number, suffix} so we can
 * animate just the numeric portion. Returns null for values with no digits.
 */
const parseValue = (value: string) => {
  const match = value.match(/^(\D*)(\d+(?:[.,]\d+)?)(.*)$/);
  if (!match) return null;
  const num = parseFloat(match[2].replace(',', '.'));
  return {
    prefix: match[1],
    target: num,
    suffix: match[3],
    isInteger: !match[2].includes('.') && !match[2].includes(','),
  };
};

export const StatCard: FC<StatCardProps> = ({
  value,
  label,
  hint,
  tooltip,
  tooltipLink,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  // Memoize so the parse result is referentially stable across re-renders
  // (otherwise the count-up effect resets every frame).
  const parsed = useMemo(() => parseValue(value), [value]);

  // If this stat label was already animated in this session, skip the
  // count-up entirely and render the final value immediately.
  const alreadyAnimated = animatedStatLabels.has(label);
  const [visible, setVisible] = useState(alreadyAnimated);
  const [displayed, setDisplayed] = useState<number>(() =>
    alreadyAnimated && parsed ? parsed.target : 0,
  );

  // Trigger animation once the card scrolls into view
  useEffect(() => {
    if (alreadyAnimated) return;
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return;
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          io.disconnect();
        }
      },
      { threshold: 0.3 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [alreadyAnimated]);

  // Animate count up with eased curve
  useEffect(() => {
    if (alreadyAnimated) return;
    if (!visible || !parsed) return;
    const target = parsed.target;
    if (target === 0) {
      setDisplayed(0);
      animatedStatLabels.add(label);
      return;
    }
    const duration = 1100;
    const start = performance.now();
    let raf = 0;
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplayed(target * eased);
      if (t < 1) {
        raf = requestAnimationFrame(tick);
      } else {
        animatedStatLabels.add(label);
      }
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [visible, parsed, label, alreadyAnimated]);

  const renderedValue = (() => {
    if (!parsed) return value;
    const current = parsed.isInteger
      ? Math.round(displayed)
      : Number(displayed.toFixed(1));
    return `${parsed.prefix}${current}${parsed.suffix}`;
  })();

  const hasTooltip = !!(tooltip || tooltipLink);

  const body = (
    <div
      ref={ref}
      className={`stat-card${visible ? ' is-visible' : ''}${hasTooltip ? ' has-tooltip' : ''}`}
      tabIndex={hasTooltip ? 0 : -1}
    >
      <div className="stat-card__icon" aria-hidden="true">
        {iconForLabel(label)}
      </div>
      <div className="stat-card__value">{renderedValue}</div>
      <div className="stat-card__label">
        {label}
        {hasTooltip && (
          <InfoCircleOutlined className="stat-card__info" aria-hidden="true" />
        )}
      </div>
      {hint && <div className="stat-card__hint">{hint}</div>}
    </div>
  );

  if (!hasTooltip) return body;

  // Internal href starts with /, otherwise treat as external.
  const isInternal = tooltipLink?.href.startsWith('/');
  const linkClass = 'stat-card__tooltip-link';
  const linkChildren = (
    <>
      {tooltipLink?.label} <ArrowRightOutlined />
    </>
  );

  const handleCtaClick = () => {
    if (tooltipLink) trackStatCtaClick(label, tooltipLink.href);
  };

  // Fire `tooltip_open` only on the first hover per mount so quick
  // mouseovers don't spam analytics. useRef survives re-renders.
  const tooltipFiredRef = useRef(false);
  const handleOpenChange = (open: boolean) => {
    if (open && !tooltipFiredRef.current) {
      tooltipFiredRef.current = true;
      trackStatTooltipOpen(label);
    }
  };

  return (
    <Tooltip
      title={
        <div className="stat-card__tooltip-body">
          {tooltip && (
            <div className="stat-card__tooltip-text">{tooltip}</div>
          )}
          {tooltipLink &&
            (isInternal ? (
              <Link
                to={tooltipLink.href}
                className={linkClass}
                onClick={handleCtaClick}
              >
                {linkChildren}
              </Link>
            ) : (
              <a
                href={tooltipLink.href}
                target="_blank"
                rel="noopener noreferrer"
                className={linkClass}
                onClick={handleCtaClick}
              >
                {linkChildren}
              </a>
            ))}
        </div>
      }
      placement="top"
      classNames={{ root: 'stat-card__tooltip' }}
      mouseEnterDelay={0.1}
      mouseLeaveDelay={0.25}
      onOpenChange={handleOpenChange}
    >
      {body}
    </Tooltip>
  );
};
